import Box from "@mui/material/Box"
import Stack from "@mui/material/Stack"
import TextField from "@mui/material/TextField"
import { DateField } from "@mui/x-date-pickers/DateField"
import DatePickerPopover from "components/DatePickerPopover/DatePickerPopover"
import { getIn, useFormikContext } from "components/Formik/forms"
import FormWrapper from "components/Formik/FormWrapper"
import StandardDialog from "components/UI/Dialog/StandardDialog"
import { toDollars } from "lib/utils"
import { orderBy, sumBy } from "lodash-es"
import { bindPopover, bindTrigger, usePopupState } from "material-ui-popup-state/hooks"
import moment, { Moment } from "moment-timezone"
import { useCallback, useEffect, useMemo, useState } from "react"
import { CurrencyAdornment, getFieldStyling } from "./AllocationsTable"
import COLORS from 'lib/colors';
import { Typography } from "@mui/material"
import IconButton from '@mui/material/IconButton'
import CloseIcon from '@mui/icons-material/Close'
import { v4 as uuidv4 } from 'uuid'
import { gql } from "generated/gql"
import { useMutation, useSuspenseQuery } from "@apollo/client"
import { CampaignSpendFieldsFragment, CampaignSpendInput, CampaignSpendUpdateInput } from '../../generated/graphql';
import { useNotifications } from "lib/NotificationsProvider"

import * as Yup from 'yup'
import Suspenseful from "components/Suspenseful"
import { useOrbit } from "providers/OrbitProvider"

const SPEND_REQUIRED_ERROR = 'Spend is required when end date is set'
const END_BEFORE_START_ERROR = 'End date must be on or after start date'
const END_REQUIRED_ERROR = 'End date is required when spend is set'

const campaignSpendSchema = Yup.object({
  id: Yup.string().nullable(),
  endTime: Yup.string().nullable(),
  spendInCents: Yup.number().nullable(),
}).test(
    'spend-required-if-endTime',
    SPEND_REQUIRED_ERROR,
    function (value) {
      if (!value) return true

      const hasEnd = !!value.endTime
      const hasSpend = typeof value.spendInCents === 'number' && !isNaN(value.spendInCents)

      return !hasEnd || hasSpend
    }
  )
  .test(
    'endTime-after-startTime',
    END_BEFORE_START_ERROR,
    function (value, { parent, path }) {
      if (!value) {
        return true
      }

      const index = parseInt(path.replace(/^\[(.*)\]/, '$1'))
      if(index === 0) {
        return true
      }

      const endTime = getIn(value, 'endTime')
      const startTime = getIn(parent, `[${index-1}].endTime`)
      if (!startTime || !endTime) return true

      return moment(endTime).isAfter(moment(startTime))
    }
  )
  .test(
    'endTime-required-if-spend',
    END_REQUIRED_ERROR,
    function (value) {
      if (!value) return true

      const hasSpend = typeof value.spendInCents === 'number' && !isNaN(value.spendInCents)
      const hasEnd = !!value.endTime

      return !hasSpend || hasEnd
    }
  )

const campaignSpendsListSchema = Yup.array().of(campaignSpendSchema)

type ExistingCampaignSpendEntry = {
  __typename?: CampaignSpendInput['__typename']
  __key?: never
  id: string
  endTime?: string
  spendInCents?: number
}

type NewCampaignSpendEntry = {
  __typename?: CampaignSpendInput['__typename']
  __key?: string
  id?: never
  endTime?: string
  spendInCents?: number
}

// A (possibly incomplete) campaign spend entry in the modal
type CampaignSpendEntry = ExistingCampaignSpendEntry | NewCampaignSpendEntry

export function isComplete(spend: CampaignSpendEntry): spend is CampaignSpendInput {
  return Boolean(
    spend.endTime &&
    typeof spend.spendInCents === 'number'
  )
}

export function shouldAppendBlankRow(endTime: string, timezone: string): boolean {
  const today = moment.tz(timezone).startOf('day')
  const endMoment = moment(endTime)
  const isToday = endMoment.isSame(today, 'day')
  const isEOM = endMoment.date() === 1
  return !isToday && !isEOM
}

export function createBlankRow(key: string): CampaignSpendEntry {
  return {
    __typename: 'CampaignSpend',
    __key: key,
  }
}

export function HoverRowContainer({
  children,
  onRemove,
  disabled,
  iconSx = {},
}) {
  return (
    <Box
      role="row"
      sx={{
        position: 'relative',
        mx: '-6px',
        my: '-6px',
        px: '6px',
        py: '6px',
        transition: 'background 0.2s',
        display: 'flex',
        alignItems: 'center',
        ...(disabled ? {} : {
          '&:hover': {
            backgroundColor: COLORS.azuraWhite,
          },
          '&:hover .hover-row-remove': {
            opacity: 1,
          },
        }),
      }}
    >
      <Box sx={{ flex: 1, mr: disabled ? 7 : undefined }}>
        {children}
      </Box>

      {onRemove && !disabled && (
        <IconButton
          onClick={onRemove}
          size="small"
          className="hover-row-remove"
          aria-label="Remove"
          sx={{
            mx: 1,
            padding: .5,
            opacity: 0,
            transition: 'opacity 0.2s',
            color: COLORS.cadetGray,
            ...iconSx,
          }}
        >
          <CloseIcon />
        </IconButton>
      )}
    </Box>
  )
}

function hasErrorAtIndex(errors: unknown[], index: number, message: string): boolean {
  return typeof errors[index] === 'string' && errors[index] === message
}

type CampaignSpendRowProps = {
  index: number
  isLast: boolean
  campaignSpend: CampaignSpendEntry
  clientTimezone: string
  startTime: string
}

function CampaignSpendRow({ index, isLast, startTime, campaignSpend, clientTimezone }: CampaignSpendRowProps) {
  const { setValues, errors, setErrors } = useFormikContext<CampaignSpendEntry[]>()
  const popupState = usePopupState({ variant: 'popover', popupId: 'endDate' })

  const endTime = campaignSpend.endTime

  const lockedStart = moment(startTime) // locked start date
  const [endDate, setEndDate] = useState<Moment | null>(endTime ? moment.tz(endTime, clientTimezone).subtract(1, 'day') : null) // end date is the day *before* the end time

  const spendCents = campaignSpend.spendInCents
  const [spendInput, setSpendInput] = useState('')

  // Update local input if Formik changes and it's out of sync
  useEffect(() => {
    const currentCents = typeof spendCents === 'number' ? spendCents : undefined
    const inputCents = spendInput === '' ? undefined : Math.round(parseFloat(spendInput) * 100)

    if (currentCents !== inputCents) {
      const next = typeof spendCents === 'number' ? (spendCents / 100).toFixed(2) : ''
      setSpendInput(next)
    }
    // Intentionally not including spendInput here to avoid this running on every keystroke
  }, [spendCents])  // eslint-disable-line react-hooks/exhaustive-deps

  const setCampaignSpend = useCallback(
    (newCampaignSpend: CampaignSpendEntry) => {
      setValues(prev => {
        const updated = [...prev]
        updated[index] = newCampaignSpend
        return updated
      })
    },
    [index, setValues]
  )

  const handleEndDateChange = useCallback(
    (selectedEndDate: Moment) => {
      // New end time is midnight on the day after the selected date
      const newEndTime = moment.tz([selectedEndDate.year(), selectedEndDate.month(), selectedEndDate.date()], clientTimezone).add(1, 'day')
      setEndDate(newEndTime.clone().subtract(1, 'day'))
      setCampaignSpend({
        ...campaignSpend,
        endTime: newEndTime.toISOString(),
      })
      popupState.close()
    },
    [clientTimezone, setCampaignSpend, campaignSpend, popupState]
  )

  const handleRemoveRow = useCallback(() => {
    setValues(prev => {
      const updated = [...prev]

      if (index >= updated.length) return updated

      // Update next row
      if (index + 1 < updated.length) {
        const nextRow = updated[index + 1]
        delete nextRow.spendInCents
      }

      // Remove the row
      updated.splice(index, 1)

      return updated
    })

    setErrors([])
  }, [index, setValues, setErrors])

  const removeSpendError = () => {
    const currentError = errors[index]

    if (currentError as string == SPEND_REQUIRED_ERROR) {
      const { [index]: _, ...rest } = errors
      const arrayErrors = Object.assign([], rest)
      setErrors(arrayErrors)
    }
  }

  const hasSpendError = hasErrorAtIndex(errors, index, SPEND_REQUIRED_ERROR)
  const hasDatesError = hasErrorAtIndex(errors, index, END_BEFORE_START_ERROR)

  return (
    <HoverRowContainer onRemove={handleRemoveRow} disabled={isLast}>
      <Stack direction='row' sx={{ flex: 1, mr: 2 }}>
        <DateField
          value={lockedStart}
          label="Date"
          inputProps={{
            "aria-label": "Start Date",
          }}
          sx={{ width: '138px' }}
          disabled={true}
          slotProps={{
            textField: {
              error: hasDatesError,
            },
          }}
        />
        <Box sx={{ lineHeight: '48px', mx: '2.5px' }}>-</Box>
        <DateField
          value={endDate}
          label="Date"
          inputProps={{
            "aria-label": "End Date",
          }}
          slotProps={{
            textField: {
              error: hasDatesError,
            },
          }}
          sx={{
            width: '138px',
            '& .MuiInputBase-root': {
              backgroundColor: hasDatesError ? COLORS.seashell : 'white',
            },
          }}
          {...bindTrigger(popupState)}
        />
        <Box sx={{ display: 'flex', flex: '1', height: '24px', borderBottom: `1px solid ${COLORS.coastalWaters}` }} />
        <TextField
          InputProps={{ startAdornment: <CurrencyAdornment /> }}
          sx={{
            ...getFieldStyling(true),
            width: '138px',
            '& .MuiInputBase-root': {
              backgroundColor: hasSpendError ? COLORS.seashell : undefined,
            },
            '& .MuiInputAdornment-root': {
              color: hasSpendError ? COLORS.copperRed : undefined,
            },
          }}
          value={spendInput}
          onChange={event => {
            const next = event.target.value

            // Allow valid decimal input
            if (next === '' || /^\d*\.?\d{0,2}$/.test(next)) {
              setSpendInput(next)

              const dollars = parseFloat(next)
              if (!isNaN(dollars)) {
                const cents = Math.round(dollars * 100)
                setCampaignSpend({ ...campaignSpend, spendInCents: cents })
              } else {
                setCampaignSpend({ ...campaignSpend, spendInCents: undefined })
              }
            }
          }}
          onBlur={() => {
            if (spendInput.trim() === '') {
              setCampaignSpend({ ...campaignSpend, spendInCents: undefined })
              return
            }
            const dollars = parseFloat(spendInput)
            const rounded = !isNaN(dollars) ? Math.round(dollars * 100) / 100 : 0
            setSpendInput(rounded.toFixed(2))
            setCampaignSpend({ ...campaignSpend, spendInCents: Math.round(rounded * 100) })
          }}
          onFocus={removeSpendError}
          placeholder="Spend"
          label="Spend"
          error={hasSpendError}
        />
      </Stack>

      <DatePickerPopover
        timezone={clientTimezone}
        initialValue={{ timeRange: [lockedStart, endDate] }}
        lockedStart={true}
        onChange={timeRange => { if(timeRange.timeRange) { handleEndDateChange(timeRange.timeRange[1]) } }}
        {...bindPopover(popupState)}
      />
    </HoverRowContainer>
  )
}

type CurrentSpendDialogProps = {
  onClose: () => void
  allocationYear: number
  allocationMonth: number
  clientTimezone: string
}

function CurrentSpendDialog({ onClose, allocationYear, allocationMonth, clientTimezone }: CurrentSpendDialogProps) {
  const { dirty, values, submitForm, isSubmitting } = useFormikContext<CampaignSpendEntry[]>()

  const beginningOfMonth = useMemo(() => moment.tz([allocationYear, allocationMonth-1, 1], clientTimezone), [allocationMonth, allocationYear, clientTimezone])
  const totalSpendInCents = sumBy(values, row => getIn(row, 'spendInCents', 0))
  const newRowKey = useMemo(() => uuidv4(), [values.length]) // eslint-disable-line react-hooks/exhaustive-deps
  const newCampaignSpend: CampaignSpendEntry | undefined = useMemo(() => {
    if(values.length === 0) {
      return createBlankRow(newRowKey)
    }
    else {
      const lastCampaignSpend = values[values.length-1]
      if(isComplete(lastCampaignSpend) && shouldAppendBlankRow(lastCampaignSpend.endTime, clientTimezone)) {
        return createBlankRow(newRowKey)
      }
    }
  }, [newRowKey, clientTimezone, values])

  const rows = [...values, ...(newCampaignSpend ? [newCampaignSpend] : [])]

  return (
    <StandardDialog
      title="Current Spend"
      description="Enter consecutive date ranges and associated spend."
      onCancel={onClose}
      onConfirm={() => submitForm()}
      onClose={onClose}
      loading={isSubmitting}
      maxWidth='600px'
      disabled={!dirty}
    >
      <Stack direction='column' gap={2} role="grid">
        {rows.map((row, index) => (
          <CampaignSpendRow
            key={row.__key ?? row.id}
            index={index}
            startTime={index === 0 ? beginningOfMonth : (rows[index-1] as CampaignSpendInput).endTime}
            campaignSpend={row}
            isLast={row === newCampaignSpend}
            clientTimezone={clientTimezone}
          />
        ))}
        <Typography aria-label="Total Spend" variant='h4' align='right' sx={{mr: 9}}>= {toDollars(totalSpendInCents)}</Typography>
      </Stack>
    </StandardDialog>
  )
}

export const GET_ALLOCATION_QUERY = gql(`
  query GetAllocation($id: ID!) {
    allocation(id: $id) {
      ...AllocationFields
    }
  }
`)


export const SAVE_CAMPAIGN_SPENDS_QUERY = gql(`
  mutation UpdateAllocation(
    $id: ID!
    $createCampaignSpends: [CampaignSpendInput!]
    $updateCampaignSpends: [CampaignSpendUpdateInput!]
    $destroyCampaignSpends: [ID!]
  ) {
    updateAllocation(input: {
      id: $id
      createCampaignSpends: $createCampaignSpends
      updateCampaignSpends: $updateCampaignSpends
      destroyCampaignSpends: $destroyCampaignSpends
    }) {
      id
      errors
      allocation {
        ...AllocationFields
      }
    }
  }
`)

// if current has no id
function getCreateCampaignSpends(current: CampaignSpendEntry[]): CampaignSpendInput[] {
  return current.filter(spend => !('id' in spend)).filter(isComplete)
}

// if current has id and is different than original
function getUpdateCampaignSpends(original: CampaignSpendFieldsFragment[], current: CampaignSpendEntry[]): CampaignSpendUpdateInput[] {
  return current
    .filter(curr => curr.id !== undefined)
    .filter(curr => {
      const match = original.find(orig => orig.id === curr.id)
      if (!match) return false

      return (
        match.spendInCents !== curr.spendInCents ||
        match.endTime !== curr.endTime
      )
  })
}

// if id exists in original and doesn't exist in current
function getDestroyCampaignSpends(original: CampaignSpendFieldsFragment[], current: CampaignSpendEntry[]): string[] {
  const currentIds = new Set(current.filter(spend => 'id' in spend).map(spend => spend.id))
  const removedIds = original.map(spend => spend.id).filter(id => !currentIds.has(id))

  return removedIds
}

export default function CampaignSpend({ onClose, allocation, refreshAllocations, clientTimezone }) {
  const { addNotification } = useNotifications()
  const { getRemoteId } = useOrbit()
  const allocationId = getRemoteId(allocation) || ''

  const { data: { allocation: allocationResult }, refetch } = useSuspenseQuery(GET_ALLOCATION_QUERY, {
    variables: { id: allocationId }
  })

  const campaignSpends : CampaignSpendFieldsFragment[] = allocationResult.campaignSpends
  const sortedCampaignSpends = orderBy(campaignSpends, ['endTime'], ['asc'])

  const [gqlSave] = useMutation(SAVE_CAMPAIGN_SPENDS_QUERY)

  const handleSave = useCallback((values: CampaignSpendEntry[], { setSubmitting }) => {
    setSubmitting(true)
    const valuesWithoutKey = values.map(({ __key, ...rest }) => rest)

    const create = getCreateCampaignSpends(valuesWithoutKey)
    const update = getUpdateCampaignSpends(sortedCampaignSpends, valuesWithoutKey)
    const destroy = getDestroyCampaignSpends(sortedCampaignSpends, valuesWithoutKey)

    const mutation = {
      id: allocationId,
      ...(create.length && { createCampaignSpends: create }),
      ...(update.length && { updateCampaignSpends: update }),
      ...(destroy.length && { destroyCampaignSpends: destroy }),
    }

    gqlSave({ variables: mutation }).then((_response) => {
      addNotification({variant: 'notice', message: `Saved current spend.`})
      refetch()
      refreshAllocations()
    }).catch(_error => {
      addNotification({variant: 'alert', message: "There was a problem saving current spend(s)." })
    }).finally(() => {
      onClose()
      setSubmitting(false)
    })
  }, [sortedCampaignSpends, allocationId, gqlSave, addNotification, refetch, refreshAllocations, onClose])

  const allocationYear = getIn(allocation, 'attributes.year')
  const allocationMonth = getIn(allocation, 'attributes.month')

  return(
    <FormWrapper data={sortedCampaignSpends} schema={campaignSpendsListSchema} onSubmit={handleSave}>
      <Suspenseful component={CurrentSpendDialog} onClose={onClose} allocationYear={allocationYear} allocationMonth={allocationMonth} clientTimezone={clientTimezone} />
    </FormWrapper>
  )
}
